import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BackToTop from "./components/BackToTop";
import ProtectedRoute from "./components/ProtectedRoute";

import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import ReferEarn from "./pages/ReferEarn";
import Tasks from "./pages/Tasks";
import WithdrawRules from "./pages/WithdrawRules";
import FAQ from "./pages/FAQ";
import Software from "./pages/Software";

import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";

import Login from "./pages/Login";
import Signup from "./pages/Signup";


/*
============================================================
PLACEHOLDER PAGE
============================================================
*/

function PlaceholderPage({ title, description }) {
  return (
    <main className="min-h-screen bg-[#f6f8fb] px-5 py-16">
      <div className="mx-auto max-w-4xl">

        <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.06)] sm:p-12">

          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
            Refrex
          </div>

          <h1 className="mt-3 text-3xl font-black tracking-[-0.04em] text-[#08111f]">
            {title}
          </h1>

          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-500">
            {description}
          </p>

        </div>
      </div>
    </main>
  );
}


/*
============================================================
NOT FOUND PAGE
============================================================
*/

function NotFound() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f6f8fb] px-5">

      <div className="text-center">

        <div className="text-7xl font-black tracking-[-0.06em] text-[#071a3b]">
          404
        </div>

        <h1 className="mt-3 text-2xl font-black text-slate-800">
          Page Not Found
        </h1>

        <p className="mt-2 text-sm text-slate-500">
          The page you are looking for does not exist.
        </p>

        <a
          href="/"
          className="mt-6 inline-flex rounded-xl bg-[#00a96d] px-5 py-3 text-sm font-black text-white transition hover:bg-[#00945f]"
        >
          Back to Home
        </a>

      </div>

    </main>
  );
}


/*
============================================================
APP CONTENT
============================================================
*/

function AppContent() {
  const location = useLocation();

  /*
  ============================================================
  DASHBOARD DETECTION
  ============================================================
  */

  const isUserDashboard =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/dashboard/");

  const isAdminDashboard =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/");

  const isStandalonePage =
    isUserDashboard || isAdminDashboard;


  return (
    <>

      {/* =====================================================
          PUBLIC NAVBAR
      ===================================================== */}

      {!isStandalonePage && <Navbar />}


      {/* =====================================================
          ROUTES
      ===================================================== */}

      <Routes>

        {/* ==================================================
            PUBLIC PAGES
        ================================================== */}

        <Route
          path="/"
          element={<Home />}
        />

        <Route
          path="/how-it-works"
          element={<HowItWorks />}
        />

        <Route
          path="/tasks"
          element={<Tasks />}
        />

        <Route
          path="/refer-earn"
          element={<ReferEarn />}
        />

        <Route
          path="/withdraw-rules"
          element={<WithdrawRules />}
        />

        <Route
          path="/faq"
          element={<FAQ />}
        />

        <Route
          path="/software"
          element={<Software />}
        />


        {/* ==================================================
            AUTH PAGES
        ================================================== */}

        <Route
          path="/signup"
          element={<Signup />}
        />

        <Route
          path="/login"
          element={<Login />}
        />


        {/* ==================================================
            USER DASHBOARD
            LOGIN REQUIRED
        ================================================== */}

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />


        {/* ==================================================
            ADMIN DASHBOARD
            LOGIN + ADMIN ROLE REQUIRED
        ================================================== */}

        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />


        {/* ==================================================
            PLACEHOLDER PAGES
        ================================================== */}

        <Route
          path="/help"
          element={
            <PlaceholderPage
              title="Help Center"
              description="Find answers and guidance for using the Refrex platform."
            />
          }
        />

        <Route
          path="/contact"
          element={
            <PlaceholderPage
              title="Contact Support"
              description="Get in touch with Refrex support for platform-related assistance."
            />
          }
        />

        <Route
          path="/terms"
          element={
            <PlaceholderPage
              title="Terms & Conditions"
              description="Review the terms and conditions applicable to the Refrex platform."
            />
          }
        />

        <Route
          path="/privacy"
          element={
            <PlaceholderPage
              title="Privacy Policy"
              description="Review how Refrex handles information and platform data."
            />
          }
        />


        {/* ==================================================
            404
        ================================================== */}

        <Route
          path="*"
          element={<NotFound />}
        />

      </Routes>


      {/* =====================================================
          PUBLIC FOOTER
      ===================================================== */}

      {!isStandalonePage && <Footer />}


      {/* =====================================================
          BACK TO TOP
      ===================================================== */}

      <BackToTop />

    </>
  );
}


/*
============================================================
ROOT APP
============================================================
*/

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;